import { Cat, KINDS } from '../../../gameplay/registry';
import { FX_ONE, FX_SHIFT, isqrt } from '../fixed';
import { Order, UState, type World } from '../world';

/** Path requests are budgeted per tick to bound worst-case simulation cost. Rules v1: searches. */
const PATH_BUDGET_PER_TICK = 48;
/**
 * Rules v2: search steps per tick (about 3 ms). A large group ordered across the map gets its paths
 * over a few ticks instead of all in one, which used to stall a frame.
 */
const PATH_EXPAND_BUDGET = 12000;
/** Rules v3: extra search steps per tick for human players' units only (see processByPriority). */
const HUMAN_EXTRA_BUDGET = 12000;

/** Request a path for unit s to (gx,gy). */
export function setDestination(w: World, s: number, gx: number, gy: number): void {
  w.destEnt[s] = -1;
  w.ordX[s] = gx;
  w.ordY[s] = gy;
  w.paths[s] = null;
  w.pathIdx[s] = 0;
  w.timer[s] = 0;
  w.moving[s] = 1;
  w.stuck[s] = 0;
  pathQueue(w).push(s);
}

/**
 * Short hop in a straight line (stepping aside and back, stepping in to a resource), with no path
 * search. Goes as far along the line as the ground is open; returns false if it cannot move at all.
 */
export function setDirect(w: World, s: number, gx: number, gy: number): boolean {
  const dx = gx - w.x[s];
  const dy = gy - w.y[s];
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / (FX_ONE >> 2)));
  let ex = w.x[s];
  let ey = w.y[s];
  for (let i = 1; i <= steps; i++) {
    const x = w.x[s] + Math.trunc((dx * i) / steps);
    const y = w.y[s] + Math.trunc((dy * i) / steps);
    if (w.blockedFor(s, x >> FX_SHIFT, y >> FX_SHIFT)) break;
    ex = x;
    ey = y;
  }
  if (ex === w.x[s] && ey === w.y[s]) return false;
  w.destEnt[s] = -1;
  w.ordX[s] = ex;
  w.ordY[s] = ey;
  w.paths[s] = [ex, ey];
  w.pathIdx[s] = 0;
  w.timer[s] = 0;
  w.moving[s] = 1;
  w.stuck[s] = 0;
  return true;
}

function pathQueue(w: World): number[] {
  return w.pathQueue;
}

/** Walk until adjacent to an entity's footprint (resources, buildings). */
export function approach(w: World, s: number, t: number): void {
  setDestination(w, s, w.x[t], w.y[t]);
  if (w.cat[t] !== Cat.Unit) w.destEnt[s] = w.idOf(t);
}

export function stopMoving(w: World, s: number): void {
  w.paths[s] = null;
  w.moving[s] = 0;
}

/** Searches one queued unit's path. Returns the search steps used, or -1 if it no longer needs one. */
function searchPath(w: World, s: number): number {
  if (!w.alive[s] || w.cat[s] !== Cat.Unit || !w.moving[s] || w.paths[s]) return -1;
  const de = w.resolve(w.destEnt[s]);
  const pf = w.pathfinderFor(s);
  const path = pf.findPath(w.x[s], w.y[s], w.ordX[s], w.ordY[s], 6000, de >= 0 ? w.footprint(de) : null);
  w.paths[s] = path.length > 0 ? path : [];
  w.pathIdx[s] = 0;
  // A tiny search space means the unit itself is boxed in; don't blame the target.
  if (de >= 0 && pf.lastUnreachable && pf.lastExpanded > 24 && w.owner[s] >= 0) {
    w.players[w.owner[s]].unreachable.set(w.destEnt[s], w.tick);
    w.paths[s] = null;
    w.moving[s] = 0;
  }
  return pf.lastExpanded;
}

function processPathRequests(w: World): void {
  const q = pathQueue(w);
  if (w.rules >= 3) return processByPriority(w, q);
  const byCount = w.rules < 2;
  let budget = byCount ? PATH_BUDGET_PER_TICK : PATH_EXPAND_BUDGET;
  let i = 0;
  for (; i < q.length && budget > 0; i++) {
    const used = searchPath(w, q[i]);
    if (used < 0) continue;
    if (byCount) {
      if (used > 0) budget--;
    } else budget -= used;
  }
  q.splice(0, i);
}

/**
 * Rules v3: human players' units are searched first, with extra search steps of their own, so an
 * order given by a person is carried out at once even while computer players have armies queued.
 * Computer players' units share what is left of the normal budget.
 */
function processByPriority(w: World, q: number[]): void {
  const human = (s: number) => w.owner[s] >= 0 && !w.players[w.owner[s]].isAI;
  const humans = q.filter(human);
  const others = q.filter((s) => !human(s));
  let humanLeft = PATH_EXPAND_BUDGET + HUMAN_EXTRA_BUDGET;
  let i = 0;
  for (; i < humans.length && humanLeft > 0; i++) {
    const used = searchPath(w, humans[i]);
    if (used > 0) humanLeft -= used;
  }
  // Humans draw on their extra allowance first; the computers get whatever of the normal budget remains.
  let aiLeft = Math.min(PATH_EXPAND_BUDGET, humanLeft);
  let j = 0;
  for (; j < others.length && aiLeft > 0; j++) {
    const used = searchPath(w, others[j]);
    if (used > 0) aiLeft -= used;
  }
  w.pathQueue = humans.slice(i).concat(others.slice(j));
}

export function updateMovement(w: World): void {
  processPathRequests(w);
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    w.px[s] = w.x[s];
    w.py[s] = w.y[s];
  }
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit) continue;
    if (!w.moving[s]) continue;
    const path = w.paths[s];
    if (!path) continue; // awaiting path
    if (w.pathIdx[s] * 2 >= path.length) {
      w.moving[s] = 0;
      w.paths[s] = null;
      if (w.state[s] === UState.Walking && w.order[s] === Order.Move) {
        w.state[s] = UState.Idle;
        w.order[s] = Order.None;
      }
      continue;
    }
    const wx = path[w.pathIdx[s] * 2];
    const wy = path[w.pathIdx[s] * 2 + 1];
    const dx = wx - w.x[s];
    const dy = wy - w.y[s];
    const dist = isqrt(dx * dx + dy * dy);
    const st = w.players[w.owner[s]].stats[w.kind[s]];
    let speed = w.groupSpeed[s] > 0 ? Math.min(w.groupSpeed[s], st.speed) : st.speed;
    // Formation movement modifiers.
    const f = w.formation[s];
    if (f === 2) speed = Math.trunc((speed * 115) / 100);
    else if (f === 3 || f === 5) speed = Math.trunc((speed * 85) / 100);
    // Terrain: shallow water slows.
    const tIdx = (w.y[s] >> FX_SHIFT) * w.size + (w.x[s] >> FX_SHIFT);
    if (w.map.terrain[tIdx] === 5 && !w.naval[s]) speed = Math.trunc((speed * 75) / 100);
    // Forest: units push through between the trees at 70% speed.
    else if (!w.naval[s] && w.isForest(tIdx)) speed = Math.trunc((speed * 70) / 100);
    const last = (w.pathIdx[s] + 1) * 2 >= path.length;
    const arrive = last ? 16 : 200;
    if (dist <= Math.max(arrive, speed)) {
      if (last) {
        moveTo(w, s, wx, wy);
      }
      w.pathIdx[s]++;
      continue;
    }
    const nx = w.x[s] + Math.trunc((dx * speed) / dist);
    const ny = w.y[s] + Math.trunc((dy * speed) / dist);
    w.fdx[s] = Math.trunc((dx * 1000) / dist);
    w.fdy[s] = Math.trunc((dy * 1000) / dist);
    const before = dist;
    moveTo(w, s, nx, ny);
    const ndx = wx - w.x[s];
    const ndy = wy - w.y[s];
    const after = isqrt(ndx * ndx + ndy * ndy);
    // Look a little ahead for idle friends standing on the line of travel, and ask them to step aside
    // before bumping into them; if already slowed to under half speed, ask whoever is in the way.
    if (after > before - (speed >> 1)) askToMakeWay(w, s, false);
    else if ((w.tick + s) % 4 === 0) askToMakeWay(w, s, true);
    if (after >= before - (speed >> 3)) {
      w.stuck[s]++;
      if (w.stuck[s] > 30) {
        // Stuck: give up if near final goal, else repath.
        const gdx = w.ordX[s] - w.x[s];
        const gdy = w.ordY[s] - w.y[s];
        if (gdx * gdx + gdy * gdy < (2 * FX_ONE) * (2 * FX_ONE) || w.stuck[s] > 90) {
          w.moving[s] = 0;
          w.paths[s] = null;
          if (w.order[s] === Order.Move) {
            w.order[s] = Order.None;
            w.state[s] = UState.Idle;
          }
        } else if (w.stuck[s] % 30 === 1) {
          w.paths[s] = null;
          pathQueue(w).push(s);
        }
      }
    } else if (w.stuck[s] > 0) w.stuck[s]--;
  }
  updateMakeWay(w);
  separate(w);
}

/** Ticks a unit stays stepped aside before heading back to its spot. */
const YIELD_TICKS = 50;

/** An idle unit that may be asked to step aside: no order, standing still, not holding position. */
function canYield(w: World, o: number): boolean {
  return w.order[o] === Order.None && !w.moving[o] && w.stance[o] !== 2;
}

/**
 * The moving unit s is being held up. Find a friendly idle unit just ahead of it and have that unit
 * step aside, perpendicular to s's heading, to whichever side it already leans towards. If both
 * sides are blocked (a narrow passage), it steps forward along s's path, out of the corridor.
 */
function askToMakeWay(w: World, s: number, lookAhead: boolean): void {
  const owner = w.owner[s];
  if (owner < 0) return;
  const rs = Math.round(KINDS[w.kind[s]].unit!.radius * FX_ONE);
  const hx = w.fdx[s];
  const hy = w.fdy[s];
  let best = -1;
  let bestD = Infinity;
  const reach = lookAhead ? FX_ONE + (FX_ONE >> 1) : FX_ONE >> 2;
  w.forUnitsInRadius(w.x[s], w.y[s], rs + FX_ONE * 2, (o, d2) => {
    if (o === s || w.naval[o] !== w.naval[s] || w.cat[o] !== Cat.Unit) return;
    const oo = w.owner[o];
    if (oo !== owner && w.areEnemies(owner, oo)) return;
    if (!canYield(w, o) || w.yieldTimer[o] > 0) return;
    const dx = w.x[o] - w.x[s];
    const dy = w.y[o] - w.y[s];
    // Only units ahead (within about 75 degrees of the heading).
    if (dx * hx + dy * hy <= 0) return;
    const ro = Math.round(KINDS[w.kind[o]].unit!.radius * FX_ONE);
    if (d2 > (rs + ro + reach) * (rs + ro + reach)) return;
    // Looking ahead, only units actually on the line of travel count.
    if (lookAhead && Math.abs(hx * dy - hy * dx) / 1000 > rs + ro) return;
    if (d2 < bestD || (d2 === bestD && o < best)) {
      bestD = d2;
      best = o;
    }
  });
  if (best < 0) return;
  const o = best;
  const ro = Math.round(KINDS[w.kind[o]].unit!.radius * FX_ONE);
  const dx = w.x[o] - w.x[s];
  const dy = w.y[o] - w.y[s];
  // Side: the one the blocker is already on relative to the mover's line (slot order breaks ties).
  const cross = hx * dy - hy * dx;
  const side = cross > 0 ? 1 : cross < 0 ? -1 : o < s ? 1 : -1;
  const step = rs + ro + Math.trunc(FX_ONE * 0.45);
  const px = Math.trunc((-hy * step) / 1000);
  const py = Math.trunc((hx * step) / 1000);
  const fwd = Math.trunc(FX_ONE * 0.25);
  const open = (x: number, y: number) => x >= 0 && y >= 0 && x < w.size * FX_ONE && y < w.size * FX_ONE && !w.blockedFor(o, x >> FX_SHIFT, y >> FX_SHIFT);
  const go = (x: number, y: number) => {
    w.yieldX[o] = w.x[o];
    w.yieldY[o] = w.y[o];
    w.yieldTimer[o] = YIELD_TICKS;
    setDirect(w, o, x, y);
  };
  // Step to one side (the near side first), slightly forward so the mover passes behind.
  // In a narrow passage neither side is open: walk on ahead until the passage widens, then step
  // aside there; failing that, simply clear the passage.
  let lastOpen: [number, number] | null = null;
  for (let k = 0; k <= 10; k++) {
    const ax = w.x[o] + Math.trunc((hx * (fwd + k * FX_ONE)) / 1000);
    const ay = w.y[o] + Math.trunc((hy * (fwd + k * FX_ONE)) / 1000);
    if (k > 0 && !open(ax, ay)) break;
    for (const sd of [side, -side]) {
      const cx = ax + sd * px;
      const cy = ay + sd * py;
      if (open(cx, cy) && open(w.x[o] + Math.trunc((cx - w.x[o]) / 2), w.y[o] + Math.trunc((cy - w.y[o]) / 2))) {
        go(cx, cy);
        return;
      }
    }
    lastOpen = [ax, ay];
  }
  if (lastOpen) go(lastOpen[0], lastOpen[1]);
}

/** Units that stepped aside return to their spot once it is clear. */
function updateMakeWay(w: World): void {
  for (let s = 0; s < w.highSlot; s++) {
    if (!w.yieldTimer[s] || !w.alive[s]) continue;
    // Any new order (which clears the timer) or a fight cancels the return trip.
    if (w.order[s] !== Order.None) {
      w.yieldTimer[s] = 0;
      continue;
    }
    if (w.moving[s] || --w.yieldTimer[s] > 1) continue;
    const rs = Math.round(KINDS[w.kind[s]].unit!.radius * FX_ONE);
    let occupied = false;
    // Wait while the spot is taken, or while friends are still on the move close by (heading back now
    // would walk straight into whoever is passing).
    w.forUnitsInRadius(w.yieldX[s], w.yieldY[s], rs * 2, (o) => {
      if (o !== s && w.naval[o] === w.naval[s]) occupied = true;
    });
    if (!occupied)
      w.forUnitsInRadius(w.x[s], w.y[s], FX_ONE * 3, (o) => {
        if (o !== s && w.moving[o] && w.naval[o] === w.naval[s] && !w.areEnemies(w.owner[o], w.owner[s])) occupied = true;
      });
    if (occupied) {
      // Someone is still there (perhaps still passing): wait a little longer.
      w.yieldTimer[s] = 20;
      continue;
    }
    w.yieldTimer[s] = 0;
    setDirect(w, s, w.yieldX[s], w.yieldY[s]);
  }
}

/** Move a unit, sliding along blocked tiles. */
function moveTo(w: World, s: number, nx: number, ny: number): void {
  const tx = nx >> FX_SHIFT;
  const ty = ny >> FX_SHIFT;
  const ox = w.x[s] >> FX_SHIFT;
  const oy = w.y[s] >> FX_SHIFT;
  // Diagonal tile transitions require both orthogonal neighbours open (no squeezing through corners).
  const diagonalSqueeze = tx !== ox && ty !== oy && (w.blockedFor(s, tx, oy) || w.blockedFor(s, ox, ty));
  if (!w.blockedFor(s, tx, ty) && !diagonalSqueeze) {
    w.x[s] = nx;
    w.y[s] = ny;
    if (!w.naval[s]) skirtTrunk(w, s, tx, ty);
    return;
  }
  if (!w.blockedFor(s, tx, oy)) {
    w.x[s] = nx;
    return;
  }
  if (!w.blockedFor(s, ox, ty)) {
    w.y[s] = ny;
  }
}

/** How far from a tree's trunk (at its tile's centre) a unit walking through the forest keeps. */
const TRUNK_CLEARANCE = (FX_ONE * 3) / 10;

/**
 * Steps a unit sideways round a tree trunk it would walk into, keeping its progress along its
 * heading, so units pass between the trees instead of through them.
 */
function skirtTrunk(w: World, s: number, tx: number, ty: number): void {
  if (!w.isForest(ty * w.size + tx)) return;
  const cx = (tx << FX_SHIFT) + (FX_ONE >> 1);
  const cy = (ty << FX_SHIFT) + (FX_ONE >> 1);
  const dx = w.x[s] - cx;
  const dy = w.y[s] - cy;
  if (dx * dx + dy * dy >= TRUNK_CLEARANCE * TRUNK_CLEARANCE) return;
  // Heading (unit vector x1000) and the unit's offset from the trunk along it and across it.
  let hx = w.fdx[s];
  let hy = w.fdy[s];
  if (hx === 0 && hy === 0) hy = 1000;
  const along = Math.trunc((dx * hx + dy * hy) / 1000);
  let across = Math.trunc((dx * hy - dy * hx) / 1000);
  const need = isqrt(Math.max(0, TRUNK_CLEARANCE * TRUNK_CLEARANCE - along * along));
  if (Math.abs(across) >= need) return;
  // Pass on whichever side the unit already is (the right if dead centre).
  across = across < 0 ? -need : need;
  w.x[s] = cx + Math.trunc((along * hx + across * hy) / 1000);
  w.y[s] = cy + Math.trunc((along * hy - across * hx) / 1000);
}

/** Resolve unit overlap with a deterministic pairwise push, and eject units from blocked tiles. */
function separate(w: World): void {
  w.rebuildUnitGrid();
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit) continue;
    const rs = Math.round(KINDS[w.kind[s]].unit!.radius * FX_ONE);
    const sx = w.x[s];
    const sy = w.y[s];
    let pushX = 0;
    let pushY = 0;
    w.forUnitsInRadius(sx, sy, rs + FX_ONE, (o) => {
      if (o === s || w.naval[o] !== w.naval[s]) return;
      const ro = Math.round(KINDS[w.kind[o]].unit!.radius * FX_ONE);
      const min = rs + ro;
      const dx = sx - w.x[o];
      const dy = sy - w.y[o];
      const d2 = dx * dx + dy * dy;
      if (d2 >= min * min) return;
      let d = isqrt(d2);
      let ux: number;
      let uy: number;
      if (d === 0) {
        // Deterministic tie-break on slot order.
        ux = s < o ? 1000 : -1000;
        uy = 0;
        d = 1;
      } else {
        ux = Math.trunc((dx * 1000) / d);
        uy = Math.trunc((dy * 1000) / d);
      }
      const overlap = min - d;
      // Idle friends give way to a moving friend; otherwise moving units yield to stationary ones.
      const friends = !w.areEnemies(w.owner[s], w.owner[o]);
      const share = friends && !w.moving[s] && w.moving[o] && canYield(w, s)
        ? 800
        : friends && w.moving[s] && !w.moving[o] && canYield(w, o)
          ? 200
          : w.moving[s] && !w.moving[o] ? 700 : !w.moving[s] && w.moving[o] ? 300 : 500;
      pushX += Math.trunc((ux * overlap * share) / 1000000);
      pushY += Math.trunc((uy * overlap * share) / 1000000);
    });
    if (pushX !== 0 || pushY !== 0) {
      const cap = FX_ONE >> 3;
      pushX = Math.max(-cap, Math.min(cap, pushX));
      pushY = Math.max(-cap, Math.min(cap, pushY));
      moveTo(w, s, sx + pushX, sy + pushY);
    }
    // Eject from blocked tile (e.g. a building placed on top of a unit).
    const tx = w.x[s] >> FX_SHIFT;
    const ty = w.y[s] >> FX_SHIFT;
    if (w.blockedFor(s, tx, ty)) {
      const ptx = w.px[s] >> FX_SHIFT;
      const pty = w.py[s] >> FX_SHIFT;
      const o = !w.blockedFor(s, ptx, pty) ? pty * w.size + ptx : w.pathfinderFor(s).nearestOpen(tx, ty, 6);
      if (o >= 0) {
        w.x[s] = ((o % w.size) << FX_SHIFT) + (FX_ONE >> 1);
        w.y[s] = (((o / w.size) | 0) << FX_SHIFT) + (FX_ONE >> 1);
      }
    }
    w.x[s] = Math.max(0, Math.min(w.size * FX_ONE - 1, w.x[s]));
    w.y[s] = Math.max(0, Math.min(w.size * FX_ONE - 1, w.y[s]));
  }
}
